import Icon from "lib/components/Icons"
import Text from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import { useContext } from "react"
import { formatStringToDateFormat, getDeliveryMethodsFromPrices } from "lib/helpers"
import { ProgramDetailProgramBySluq } from "@graphql/program/program_by_slug"

interface DateAndTimeProps {
  programDetail: ProgramDetailProgramBySluq
  classSchedules?: any
  noPadding: boolean
}

function DateAndTime({ programDetail, classSchedules = [], noPadding = false }: DateAndTimeProps) {
  const { useTranslation: t } = useContext(LanguageContext)

  const getCertificationDeliveryLabel = (programType: "class" | "assessment" | "refreshment") => {
    const { hasOnline, hasOffline } = getDeliveryMethodsFromPrices(programDetail?.prices || [], programType)
    if (hasOnline && hasOffline) return `${t("online")}/${t("offline")}`
    if (hasOnline) return t("online")
    if (hasOffline) return t("offline")
    return "-"
  }

  const getAssessmentRefreshmentLabel = () => {
    const hasAssessment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "assessment",
    )
    const hasRefreshment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "refreshment",
    )

    if (hasAssessment && hasRefreshment) return t("assessment_or_refreshment")
    if (hasAssessment) return t("assessment")
    return t("refreshment")
  }

  const getAssessmentRefreshmentDeliveryLabel = () => {
    const hasAssessment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "assessment",
    )
    const hasRefreshment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "refreshment",
    )

    let hasOnline = false
    let hasOffline = false

    if (hasAssessment) {
      const methods = getDeliveryMethodsFromPrices(programDetail?.prices || [], "assessment")
      if (methods.hasOnline) hasOnline = true
      if (methods.hasOffline) hasOffline = true
    }
    if (hasRefreshment) {
      const methods = getDeliveryMethodsFromPrices(programDetail?.prices || [], "refreshment")
      if (methods.hasOnline) hasOnline = true
      if (methods.hasOffline) hasOffline = true
    }

    if (hasOnline && hasOffline) return `${t("online")}/${t("offline")}`
    if (hasOnline) return t("online")
    if (hasOffline) return t("offline")
    return "-"
  }

  const renderCertificationSchedule = () => {
    const scheduleItems = []
    const classType = programDetail?.program_detail_types?.find(
      (type) => type.program_type === "class",
    )
    const hasAssessment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "assessment",
    )
    const hasRefreshment = programDetail?.program_detail_types?.some(
      (type) => type.program_type === "refreshment",
    )

    // Render class days - use syllabuses if available, otherwise use duration_day
    if (classType) {
      const hasSyllabuses = programDetail?.program_detail_syllabuses?.length > 0
      const classDaysCount = hasSyllabuses
        ? new Set(programDetail.program_detail_syllabuses.map((s) => s.day_index)).size
        : classType.duration_day || 1

      const classItems = Array.from({ length: classDaysCount }, (_, i) => (
        <div
          className="bg-neutral-20 p-3 rounded-md flex flex-row items-center justify-between"
          key={`class-${i}`}>
          <div className="flex flex-col gap-y-[6px]">
            <Text weight="semibold" size="small">
              {t("class")} - {getCertificationDeliveryLabel("class")}
            </Text>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-y-[6px] md:gap-y-0 gap-x-0 md:gap-x-2">
              <div className="flex flex-row items-center gap-x-1">
                <Icon iconName="date2" height={16} width={16} />
                <Text weight="medium" size="extra-small">
                  {t("nearest_available_slot_from_the_request")}
                </Text>
              </div>
              <div className="flex flex-row items-center gap-x-1">
                <Icon iconName="time" height={16} width={16} />
                <Text weight="medium" size="extra-small">
                  {classType.duration_hour}{" "}
                  {classType.duration_hour > 1 ? t("hours").toLowerCase() : t("hour").toLowerCase()}
                </Text>
              </div>
            </div>
          </div>
          <div className="bg-info-surface border border-primary-border rounded-lg px-2 py-1 w-fit h-full">
            <Text size="extra-small" weight="semibold" color="dark">
              {t("day").toUpperCase()} {i + 1}
            </Text>
          </div>
        </div>
      ))
      scheduleItems.push(...classItems)
    }

    // Render assessment and/or refreshment
    if (hasAssessment || hasRefreshment) {
      const assessmentType = programDetail?.program_detail_types?.find(
        (type) => type.program_type === "assessment" || type.program_type === "refreshment",
      )

      const hasSyllabuses = programDetail?.program_detail_syllabuses?.length > 0

      let classDaysCount = 0
      if (classType) {
        classDaysCount = hasSyllabuses
          ? new Set(programDetail.program_detail_syllabuses.map((s) => s.day_index)).size
          : classType.duration_day || 0
      }
      const durationDays = assessmentType?.duration_day || 1

      const assessmentItems = Array.from({ length: durationDays }, (_, i) => (
        <div
          className="bg-neutral-20 p-3 rounded-md flex flex-row items-center justify-between"
          key={`assessment-refreshment-${i}`}>
          <div className="flex flex-col gap-y-[6px]">
            <Text weight="semibold" size="small">
              {getAssessmentRefreshmentLabel()} - {getAssessmentRefreshmentDeliveryLabel()}
            </Text>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-y-[6px] md:gap-y-0 gap-x-0 md:gap-x-2">
              <div className="flex flex-row items-center gap-x-1">
                <Icon iconName="date2" height={16} width={16} />
                <Text weight="medium" size="extra-small">
                  {t("nearest_available_slot_from_the_request")}
                </Text>
              </div>
              <div className="flex flex-row items-center gap-x-1">
                <Icon iconName="time" height={16} width={16} />
                <Text weight="medium" size="extra-small">
                  {assessmentType?.duration_hour}{" "}
                  {assessmentType?.duration_hour > 1
                    ? t("hours").toLowerCase()
                    : t("hour").toLowerCase()}
                </Text>
              </div>
            </div>
          </div>
          <div className="bg-info-surface border border-primary-border rounded-lg px-2 py-1 w-fit h-full">
            <Text size="extra-small" weight="semibold" color="dark">
              {t("day").toUpperCase()} {classDaysCount + i + 1}
            </Text>
          </div>
        </div>
      ))
      scheduleItems.push(...assessmentItems)
    }

    return scheduleItems
  }

  return (
    <div className={noPadding ? "mt-4" : "mt-14"}>
      {programDetail?.program?.type === "certification" && (
        <div className="flex flex-col gap-y-2">{renderCertificationSchedule()}</div>
      )}
      {programDetail?.program?.type === "public" && (
        <div className="flex flex-col gap-y-2">
          {classSchedules.map((schedule, index) => (
            <div
              className="bg-neutral-20 p-3 rounded-md flex flex-row items-center justify-between"
              key={index}>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-y-[6px] md:gap-y-0 gap-x-0 md:gap-x-4">
                <div className="flex flex-row items-center gap-x-0 md:gap-x-1">
                  <Icon iconName="date2" height={16} />
                  <Text weight="medium" size="extra-small">
                    {t(formatStringToDateFormat(schedule?.daily_start_time, "dddd").toLowerCase())},{" "}
                    {formatStringToDateFormat(schedule?.daily_start_time, "DD")}{" "}
                    {t(formatStringToDateFormat(schedule?.daily_start_time, "MMMM").toLowerCase())}{" "}
                    {formatStringToDateFormat(schedule?.daily_start_time, "YYYY")}
                  </Text>
                </div>
                <div className="flex flex-row items-center md:mr-[9px] gap-x-0 md:gap-x-1">
                  <Icon iconName="time" height={16} />
                  <Text weight="medium" size="extra-small">
                    {`${formatStringToDateFormat(
                      schedule?.daily_start_time,
                      "HH:mm",
                    )} - ${formatStringToDateFormat(schedule?.daily_end_time, "HH:mm")}`}{" "}
                    WIB
                  </Text>
                </div>
              </div>
              {classSchedules.length > 1 && (
                <div className="bg-info-surface border border-primary-border rounded-lg px-2 py-1 w-fit h-full">
                  <Text size="extra-small" weight="semibold" color="dark">
                    {t("day").toUpperCase()} {index + 1}
                  </Text>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DateAndTime
